import {
	type TimelineJsonOptions,
	type ChartPayload,
	type SlideContent,
	type CarouselTheme
} from '@soomo/lib/types/index';
import { type ScoreSettingsConfig } from '@soomo/lib/components/pageElements/GoReact/types';
import { type PollResultDisplayStyle } from '@soomo/lib/components/pageElements/PollQuestion/types';

import {
	type PollDataType,
	type PollDataGroupByType,
	type PollChoiceOrderingType,
	type PollColorGroupByType,
	type PollChartType
} from '@editor2/containers/PageElementsPage/components/PollElement/types';
import { type QuestionGenerationPayload } from './apiPayloads';

export enum ElementType {
	HTMLText = 'NG::Soomo::Text',
	MCQuestion = 'NG::Soomo::MC::Question',
	MCChoice = 'NG::Soomo::MC::Choice',
	MCQuestionPool = 'NG::Soomo::MC::QuestionPool',
	Figure = 'NG::Soomo::Figure',
	GeoMap = 'NG::Soomo::GeoMap',
	Poll = 'NG::Soomo::Poll::Question',
	PollChoice = 'NG::Soomo::Poll::Choice',
	PollResults = 'NG::Soomo::Poll::Results',
	SAQuestion = 'NG::Soomo::Question',
	YourTurn = 'NG::Soomo::QuestionSet',
	Builder = 'NG::Soomo::Builder',
	Artifact = 'NG::Artifacts::Article',
	ArtifactArticle = 'NG::Artifacts::Article',
	ArtifactAudio = 'NG::Artifacts::Audio',
	ArtifactVideo = 'NG::Artifacts::Video',
	ArtifactDownload = 'NG::Artifacts::Download',
	ArtifactPdfDownload = 'NG::Artifacts::PdfDownload',
	ArtifactDocxDownload = 'NG::Artifacts::DocxDownload',
	ArtifactXlsxDownload = 'NG::Artifacts::XlsxDownload',
	Chart = 'NG::Soomo::Chart',
	PopupQuiz = 'NG::Soomo::Popup::Quiz',
	ExternalTool = 'NG::Soomo::ExternalTool',
	NestingInstruction = 'NG::Soomo::NestingInstruction',
	QuestionDeck = 'NG::Soomo::QuestionDeck',
	Decision = 'NG::Soomo::Decision',
	VoiceRecording = 'NG::Soomo::VoiceRecording',
	PageProgress = 'NG::Soomo::PageProgress',
	Timeline = 'NG::Soomo::Timeline',
	Cosmo = 'NG::Soomo::Cosmo',
	GetTheGist = 'NG::Soomo::MC::QuestionSet',
	AiChatbox = 'NG::Soomo::AiChatbox',
	EocCheckinChatbot = 'NG::Soomo::EocCheckinChatbot',
	Carousel = 'NG::Soomo::Carousel',
	Slide = 'NG::Soomo::Carousel::Slide'
}

export enum ElementConfigName {
	'NG::Soomo::Text' = 'HTMLText',
	'NG::Soomo::MC::Question' = 'MCQuestion',
	'NG::Soomo::MC::Choice' = 'MCChoice',
	'NG::Soomo::MC::QuestionPool' = 'MCQuestionPool',
	'NG::Soomo::Figure' = 'Figure',
	'NG::Soomo::GeoMap' = 'GeoMap',
	'NG::Soomo::Poll::Question' = 'Poll',
	'NG::Soomo::Poll::Choice' = 'PollChoice',
	'NG::Soomo::Poll::Results' = 'PollResults',
	'NG::Soomo::Question' = 'SAQuestion',
	'NG::Soomo::QuestionSet' = 'YourTurn',
	'NG::Soomo::Builder' = 'Builder',
	'NG::Artifacts::Article' = 'Artifact',
	'NG::Artifacts::Audio' = 'Artifact',
	'NG::Artifacts::Video' = 'Artifact',
	'NG::Artifacts::Download' = 'Artifact',
	'NG::Artifacts::PdfDownload' = 'Artifact',
	'NG::Artifacts::DocxDownload' = 'Artifact',
	'NG::Artifacts::XlsxDownload' = 'Artifact',
	'NG::Soomo::Chart' = 'Chart',
	'NG::Soomo::Popup::Quiz' = 'PopupQuiz',
	'NG::Soomo::ExternalTool' = 'ExternalTool',
	'NG::Soomo::NestingInstruction' = 'NestingInstruction',
	'NG::Soomo::QuestionDeck' = 'QuestionDeck',
	'NG::Soomo::Decision' = 'Decision',
	'NG::Soomo::VoiceRecording' = 'VoiceRecording',
	'NG::Soomo::PageProgress' = 'PageProgress',
	'NG::Soomo::Timeline' = 'Timeline',
	'NG::Soomo::Cosmo' = 'Cosmo',
	'NG::Soomo::MC::QuestionSet' = 'GetTheGist',
	'NG::Soomo::AiChatbox' = 'AiChatbox',
	'NG::Soomo::EocCheckinChatbot' = 'EocCheckinChatbot',
	'NG::Soomo::Carousel' = 'Carousel',
	'NG::Soomo::Carousel::Slide' = 'Slide'
}

interface ElementWithId {
	id: number;
	indent: boolean;
	initPageId: string;
}

interface ElementWithExactMatchValidations {
	exactMatchOverride: boolean;
}

export interface HTMLPayload {
	family_id: string;
	body: string;
	metadata: any;
	type: ElementType.HTMLText;
}

export interface HTML extends HTMLPayload, ElementWithId {}

export enum FigureType {
	figure = 'figure',
	table = 'table',
	image = 'image',
	video = 'video',
	audio = 'audio',
	iframe = 'iframe',
	html = 'html',
	big_quote_epigraph = 'big_quote_epigraph'
}

export enum FigureSubType {
	generic = 'generic',
	vzaar = 'vzaar',
	akamai = 'akamai',
	castlabs = 'castlabs',
	brightcove = 'brightcove',
	kaltura = 'kaltura',
	vimeo = 'vimeo'
}

export enum FigurePosition {
	'normal' = 'normal',
	'pull-left' = 'pull-left',
	'pull-right' = 'pull-right'
}
export enum HeaderStyle {
	traditional = 'traditional',
	none = 'none'
}

export interface FigurePayload {
	family_id: string;
	metadata: any;
	type: ElementType.Figure;
	figure_type: FigureType;
	figure_subtype?: FigureSubType;
	figure_position: FigurePosition;
	figure_number: string;
	title: string;
	payload: string;
	payload_metadata: {
		vzaar_shared_secret?: string;
		requires_signing?: boolean | string;
		offline?: boolean;
		map_json_data_url?: string;
	};
	description: string;
	caption: string;
	credits: string;
	transcript_url: string;
	instructions: string;
	citation: string;
	map_json_data: any;
	figure_backup_url: string;
	header_style: HeaderStyle;
	quote_author: string;
	quote_citation: string;
	quote_text: string;
	disable_expanded_view: boolean;
}

export interface Figure extends FigurePayload, ElementWithId {}

export enum GeoMapType {
	US = 'us',
	World = 'world'
}

export interface GeoMapColor {
	name: string;
	color: string;
	id: string;
}

export interface GeoMapData {
	[key: string]: (string | number)[];
}

export enum GeoMapScaleType {
	default = 'default',
	custom = 'custom'
}

export interface GeoMapPayload {
	family_id: string;
	metadata: any;
	type: ElementType.GeoMap;

	geomap_type: GeoMapType;
	figure_number: string;
	title: string;

	data: GeoMapData;
	labels: string[];
	colors: GeoMapColor[];
	tooltip: string;

	margins: [string, string, string, string];

	scale?: {
		type: GeoMapScaleType;
		value: number;
	};
	translate?: {
		type: GeoMapScaleType;
		value: [number, number];
	};

	description: string;
	caption: string;
	credits: string;
	instructions: string;
}

export interface GeoMap extends GeoMapPayload, ElementWithId {}

export interface MCQuestionPayload {
	family_id: string;
	body: string;
	disabled: boolean;
	choices: MCChoicePayload[];
	metadata: any;
	type: ElementType.MCQuestion;
}

export interface MCQuestion extends MCQuestionPayload, ElementWithId {
	choices: MCChoice[];
}

export interface MCChoicePayload {
	family_id: string;
	body: string;
	rejoinder: string;
	is_correct: boolean;
	metadata: any;
	type: ElementType.MCChoice;
}

export interface MCChoice extends MCChoicePayload, ElementWithId {}

export interface MCQuestionPoolPayload {
	family_id: string;
	questions: MCQuestion[];
	type: ElementType.MCQuestionPool;
	metadata: { [key: string]: string };
	isGeneratingQuestions?: boolean;
	needsQuestionFeedback?: boolean;
	questionGenerationPayload?: QuestionGenerationPayload;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	questionGenerationResponse?: any;
}

export interface MCQuestionPool extends MCQuestionPoolPayload, ElementWithId {}

export interface SAQuestionValidation {
	clientId?: string;
	source?: string;
	name: string;
	enabled: boolean;
	parameters: {
		minimum?: number;
		maximum?: number;
		'match-expression'?: string;
		'partial-match'?: boolean;
		reject?: boolean;
	};
	rejoinders: string[];
	element_override: boolean;
}

export interface SAQuestionPayload {
	family_id: string;
	body: string;
	reference_answer: string;
	disabled: boolean;
	validations: SAQuestionValidation[];
	editable: boolean;
	auto_save: any;

	metadata: any;
	type: ElementType.SAQuestion;
}

export interface SAQuestion
	extends SAQuestionPayload,
		ElementWithId,
		ElementWithExactMatchValidations {}

export interface QuestionPayload {
	family_id: string;
	body: string;
	reference_answer: string;
	disabled: boolean;
	metadata: any;
	type: ElementType.SAQuestion;
}

export interface ResponseBoardPayload {
	family_id: string;
	validations: SAQuestionValidation[];
	questions: QuestionPayload[];
	editable: boolean;
	auto_save: any;

	metadata: any;
	type: ElementType.YourTurn;
}

export interface ResponseBoard
	extends ResponseBoardPayload,
		ElementWithId,
		ElementWithExactMatchValidations {}

export interface PollChoicePayload {
	family_id: string;
	metadata: any;
	body: string;
	shortened_body?: string;
	type: ElementType.PollChoice;
}

export interface PollChoice extends PollChoicePayload, ElementWithId {}

export interface PollPayload {
	family_id: string;
	metadata: any;
	body: string;
	type: ElementType.Poll;
	choices: PollChoice[];
	chart_type: PollChartType;
	data?: (string[] | number[])[];
	total_count?: number[];
	data_type?: PollDataType;
	data_group_by?: PollDataGroupByType;
	choice_ordering: PollChoiceOrderingType;
	source_dataset_index?: number;
	contextual_info?: string[];
	color_palette?: string;
	color_group_by: PollColorGroupByType;
	instructions: string;
	caption: string;
	description: string;
	credits: string;
}

export interface Poll extends PollPayload, ElementWithId {}

export interface PollResultsPayload {
	family_id: string;
	poll_question_family_id: string;
	type: ElementType.PollResults;
}

export interface PollResults extends PollResultsPayload, ElementWithId {}

export interface ArtifactPayload {
	family_id: string;
	metadata: any;
	type:
		| ElementType.ArtifactArticle
		| ElementType.ArtifactAudio
		| ElementType.ArtifactVideo
		| ElementType.ArtifactDownload
		| ElementType.ArtifactPdfDownload
		| ElementType.ArtifactDocxDownload
		| ElementType.ArtifactXlsxDownload;
	title: string;
	artifact_url: string;
	embed_code: string;
	transcript_url: string;
	credits: string;
	citation: string;
	artifact_backup_url: string;
}

export interface Artifact extends ArtifactPayload, ElementWithId {}

export interface BuilderPayload {
	family_id: string;
	metadata: any;
	type: ElementType.Builder;
	element_definition_key?: string;
	config: string;
}

export interface Builder extends BuilderPayload, ElementWithId {}

export interface PopupQuizPayload {
	family_id: string;
	metadata: any;
	type: ElementType.PopupQuiz;
	config?: string;
	ui?: any;
	instructions?: any;
	questions?: any[];
	score_interpretations?: any;
	disable_review?: any;
	mode?: any;
	assessment_data?: any;
	dimensions?: any;
	results_type?: any;
}

export interface PopupQuiz extends PopupQuizPayload, ElementWithId {}

export interface ExternalToolPayload {
	family_id: string;
	metadata: any;
	type: ElementType.ExternalTool;
	tool_name?: string;
	config?: string;
	caption?: string;
	credits?: string;
	transcript_url?: string;
}

export interface GoReactExternalToolPayload extends ExternalToolPayload {
	title: string;
	score_config: ScoreSettingsConfig;
	prompt_body: string;
}

export type GoReactExternalTool = GoReactExternalToolPayload & ElementWithId;

export type ExternalTool = (ExternalToolPayload & ElementWithId) | GoReactExternalTool;

export interface NestingInstructionPayload {
	family_id: string;
	metadata: any;
	type: ElementType.NestingInstruction;
	direction: 'in' | 'out';
	container_classes: string[];
}

export interface NestingInstruction extends NestingInstructionPayload, ElementWithId {}

export interface QuestionDeckPayload {
	family_id: string;
	metadata: any;
	type: ElementType.QuestionDeck;
	direction: 'in' | 'out';
	poll_alias_result_display_style?: PollResultDisplayStyle;
}

export interface QuestionDeck extends QuestionDeckPayload, ElementWithId {}

export interface DecisionPayload {
	family_id: string;
	metadata: any;
	type: ElementType.Decision;
	name: string;
	prompt: string;
}

export interface Decision extends DecisionPayload, ElementWithId {}

export interface VoiceRecordingPayload {
	family_id: string;
	metadata: any;
	type: ElementType.VoiceRecording;
	autosubmit: boolean;
	phone: string;
	instructions: string;
	column_name: string;
	filename_template: string;
	lms_name: string;
	autosubmit_button_label: string;
}

export interface VoiceRecording extends VoiceRecordingPayload, ElementWithId {}

export interface PageProgressPayload {
	family_id: string;
	metadata: any;
	type: ElementType.PageProgress;
	target_page_family_id: string;
	min_time_spent_estimation_threshold: string;
	active_prediction_text: string;
	passive_prediction_text: string;
	active_prediction_bottom_text: string;
}

export interface PageProgress extends PageProgressPayload, ElementWithId {}

export interface TimelineDate {
	year: number;
	month?: number;
	day?: number;
	hour?: number;
	minute?: number;
	second?: number;
	millisecond?: number;
	display_date?: string;
}
export interface TimelineText {
	headline?: string;
	text?: string;
}
export interface TimelineMedia {
	url: string;
	caption?: string;
	credit?: string;
	thumbnail?: string;
	alt?: string;
	title?: string;
	link?: string;
	link_target?: string;
}
export interface TimelineBackground {
	url?: string;
	color?: string;
	alt?: string;
}
export interface TimelineEra {
	start_date: TimelineDate;
	end_date: TimelineDate;
	text?: TimelineText;
}
export enum TimelineScale {
	human = 'human',
	cosmological = 'cosmological'
}

export enum TimelineDateFormat {
	'Full date and time' = 'yyyy/MM/dd h:mm aa',
	'Full date' = 'yyyy/MM/dd',
	'Year and month' = 'yyy MMMM',
	'Year' = 'yyyy'
}
export interface TimelineSlide {
	start_date: TimelineDate; // required except for title slide
	start_date_format?: TimelineDateFormat;
	end_date?: TimelineDate;
	end_date_format?: TimelineDateFormat;
	text?: TimelineText;
	media: TimelineMedia;
	group?: string;
	display_date?: string;
	background?: TimelineBackground;
	autolink?: boolean;
	unique_id?: string;
}
export interface TimelineJsonData {
	events: TimelineSlide[];
	title?: TimelineSlide;
	eras?: TimelineEra[];
	scale?: TimelineScale;
}

export interface TimelinePayload {
	family_id: string;
	metadata: any;
	type: ElementType.Timeline;
	data: TimelineJsonData;
	figure_number: string;
	title: string;
	description: string;
	caption: string;
	credits: string;
	instructions: string;
	touchInstructions: string;
	options?: TimelineJsonOptions;
}

export interface Timeline extends TimelinePayload, ElementWithId {}

export interface Chart extends Omit<ChartPayload, 'tippyProps'>, ElementWithId {
	family_id: string;
	metadata: { [key: string]: string };
}

export interface CarouselSlidePayload {
	family_id: string;
	type: ElementType.Slide;
	hide_logo?: boolean;
	content: SlideContent;
}
export interface CarouselSlide extends CarouselSlidePayload, ElementWithId {}

export interface CarouselPayload {
	family_id: string;
	metadata: any;
	type: ElementType.Carousel;
	slides: CarouselSlide[];
	theme: CarouselTheme;
	color: string;
	logo_url?: string;
	logo_alt?: string;
	figure_number: string;
	title: string;
	caption: string;
	credits: string;
}

export interface Carousel extends CarouselPayload, ElementWithId {}

export interface Cosmo {
	family_id: string;
	id?: number;
	metadata: any;
	type: ElementType.Cosmo;
	indent: boolean;

	initPageId: string;
}

export interface GetTheGist {
	family_id: string;
	id?: number;
	metadata: any;
	type: ElementType.GetTheGist;
	indent: boolean;

	initPageId: string;
}

export interface AiChatboxPayload {
	family_id: string;
	type: ElementType.AiChatbox;
	title: string;
	config: string; // serialized JSON object
}

export type AiChatbox = AiChatboxPayload & Partial<ElementWithId>;

/**
 * A single author-supplied key concept ("seed") for an EOC check-in chatbot.
 * `concept` is the idea the student must demonstrate; `description` is an
 * optional success criterion shown to the tutor. The runtime-owned `status`
 * and `reason` fields are never authored here (see
 * `NG::Soomo::EocCheckinChatbot` in the soomo app).
 */
export interface KeyConcept {
	concept: string;
	description: string;
}

export interface EocCheckinChatbotPayload {
	family_id: string;
	metadata: any;
	type: ElementType.EocCheckinChatbot;
	// Authored content may store concepts as plain strings or as objects; the
	// editor authors objects but tolerates both shapes when loading.
	key_concepts: Array<KeyConcept | string>;
	max_questions?: number | null;
	time_limit_seconds?: number | null;
	// Transient, editor-only flag shown while concepts are being generated by the
	// Lambda. Stripped before saving (see unsavedChangesService).
	isGeneratingConcepts?: boolean;
}

export interface EocCheckinChatbot extends EocCheckinChatbotPayload, ElementWithId {}

export type DoubleElements = NestingInstruction | QuestionDeck;

export type PageElement =
	| HTML
	| MCQuestion
	| MCChoice
	| MCQuestionPool
	| SAQuestion
	| ResponseBoard
	| Figure
	| GeoMap
	| Chart
	| Poll
	| PollResults
	| Artifact
	| Builder
	| PopupQuiz
	| ExternalTool
	| NestingInstruction
	| QuestionDeck
	| Decision
	| VoiceRecording
	| PageProgress
	| Timeline
	| Carousel
	| CarouselSlide
	| Cosmo
	| GetTheGist
	| AiChatbox
	| EocCheckinChatbot;
